import { H5, InputText } from "@sellar-io/components";
import { ListElement } from "../../../../components";
import type {
  NextPayoutResponseType,
  PayoutListResponseType,
} from "@sellar-io/api-v2/types";
import { twMerge } from "tailwind-merge";
import { useRef, useState } from "react";

import dayjs from "dayjs";
import { usePayoutList } from "../../../../hooks/api/v2/payout/usePayoutList";
import {
  BadgeGreen,
  BadgeWhiteFill,
  BadgeRed,
  BadgeZinc,
} from "../../../../components/badge/Badge";
import { formatCurrency, formatDateRange } from "@sellar-io/common";
import { InfiniteList } from "../../../../components/infiniteLoading/InfiniteList";
import { InputContainer } from "@sellar-io/components";
import { useDebounce } from "use-debounce";
import { SELLAR_BUSINESS_ID } from "../../../../constants/sellarConstants.shared";
import { getEmptyMessage, getLastFriday } from "./SellarCreditTable.shared";
import { SellarCreditStats } from "./SellarCreditStats";

const SellarCreditCard = ({
  payoutItem,
  setSelectedPayout,
  setActive,
}: {
  payoutItem: PayoutListResponseType[number];
  setSelectedPayout: (payout: PayoutListResponseType[number]) => void;
  setActive: (value: boolean) => void;
}) => {
  const { total } = payoutItem.Invoices.find(
    (invoice) =>
      invoice.type === "sellarCreditSupplier" &&
      invoice.SenderBusinessId === SELLAR_BUSINESS_ID,
  ) || { total: 0 };

  return (
    <ListElement
      onClick={() => {
        setSelectedPayout(payoutItem);
        setActive(true);
      }}
    >
      <div className={"flex flex-col gap-1"}>
        <span className="text-xs">PAY #{payoutItem.id}</span>
        <H5>
          {payoutItem.createdAt
            ? formatDateRange(getLastFriday(payoutItem.createdAt).toDate(), 7)
            : "NA"}
        </H5>
        {payoutItem.amount && (
          <div className="text-sm">
            <span>Gross: {formatCurrency(payoutItem.amount + total)} </span>
            <span className="font-semibold">&bull; </span>
            <span>Fee: {formatCurrency(total)} </span>
            <span className="font-semibold">&bull; </span>
            <span>Net: {formatCurrency(payoutItem.amount)}</span>
          </div>
        )}
        <div className="mt-2">
          {getBadgeColor(payoutItem.status, payoutItem.payoutDate || null)}
        </div>
      </div>
    </ListElement>
  );
};

function getBadgeColor(
  status:
    | "optimistic"
    | "draft"
    | "paid"
    | "failed"
    | "issued"
    | "payment_processing",
  payoutDate: Date | null,
) {
  switch (status) {
    case "draft":
      return (
        <BadgeWhiteFill className="text-sm font-medium">Draft</BadgeWhiteFill>
      );
    case "payment_processing":
      return (
        <BadgeZinc className="text-sm font-medium">
          Payment processing
        </BadgeZinc>
      );
    case "paid":
      return (
        <BadgeGreen className="text-sm font-medium">
          Paid: {dayjs(payoutDate).format("DD/MM/YYYY")}
        </BadgeGreen>
      );
    case "failed":
      return <BadgeRed className="text-sm font-medium">Failed</BadgeRed>;
    default:
      return status;
  }
}

export const SellarCreditsCardList = ({
  setSelectedPayout,
  setActive,
}: {
  setSelectedPayout: (
    SellarCredit: PayoutListResponseType[number] | NextPayoutResponseType,
  ) => void;
  setActive: (value: boolean) => void;
}) => {
  const [search, setSearch] = useState("");
  const [debouncedSearchValue] = useDebounce(search, 500);

  const sellarCreditsQuery = usePayoutList({
    limit: 10,
    search: debouncedSearchValue,
  });
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  return (
    <div
      className={twMerge(
        "[scrollbar-width:none] overflow-auto border-zinc-300 [::-webkit-scrollbar]:hidden",
      )}
      ref={scrollContainerRef}
    >
      <div
        className={twMerge(
          "flex w-full border-collapse flex-wrap items-center justify-between",
        )}
      >
        <div className="w-full overflow-x-auto bg-white px-4 pb-2">
          <SellarCreditStats
            setSelectedPayout={setSelectedPayout}
            setActive={setActive}
          />
        </div>
        <div className="flex w-full border-b border-zinc-300 bg-zinc-50 p-4">
          <InputContainer className="mr-2 w-full min-w-[300px] py-[0.4rem]">
            <InputText
              required={false}
              placeholder="Search PAY #..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full"
            />
          </InputContainer>
        </div>
      </div>
      {sellarCreditsQuery.data?.pages &&
        sellarCreditsQuery.data.pages.length === 0 && (
          <div className={"flex flex-col gap-1"}>
            <H5>{getEmptyMessage(search, "all")}</H5>
          </div>
        )}
      <InfiniteList query={sellarCreditsQuery}>
        {sellarCreditsQuery.data?.pages.map((page) =>
          page.map((SellarCreditItem) => (
            <SellarCreditCard
              payoutItem={SellarCreditItem}
              key={SellarCreditItem.id}
              setSelectedPayout={setSelectedPayout}
              setActive={setActive}
            />
          )),
        )}
      </InfiniteList>
    </div>
  );
};
